// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import type { TriageStage } from '@polarissleep/scheduling';
import { mapPatientTriageStatusToTriageStage } from '@polarissleep/scheduling';
import { POLARIS_TRIAGE_STATUS_TAG_SYSTEM } from '@polarissleep/shared';
import { useCallback, useEffect, useState } from 'react';

interface UsePatientTriageStageResult {
  triageStage: TriageStage | undefined;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

/**
 * Resolves the current triage stage for the given patient by reading the
 * triage-status tag (`POLARIS_TRIAGE_STATUS_TAG_SYSTEM`) — the same source the
 * chart-header "Triage Status" dropdown and the triage queues write. Used by the
 * provider-app Appointments tab to drive the Care Journey Stepper. The Patient is
 * re-read fresh so the stage reflects the latest status edit.
 *
 * @param patient - Patient resource (must have `id`)
 * @returns The resolved triage stage, loading / error state, and a refresh fn
 */
export function usePatientTriageStage(patient: Patient | undefined): UsePatientTriageStageResult {
  const medplum = useMedplum();
  const [triageStage, setTriageStage] = useState<TriageStage | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchStage = useCallback(async () => {
    if (!patient?.id) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const fresh = (await medplum.readResource('Patient', patient.id)) as Patient;
      const triageStatusCode = fresh.meta?.tag?.find((t) => t.system === POLARIS_TRIAGE_STATUS_TAG_SYSTEM)?.code;
      setTriageStage(mapPatientTriageStatusToTriageStage(triageStatusCode));
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load triage stage'));
    } finally {
      setLoading(false);
    }
  }, [medplum, patient?.id]);

  useEffect(() => {
    fetchStage().catch(console.error);
  }, [fetchStage]);

  return { triageStage, loading, error, refresh: fetchStage };
}
